// P:\0PJ\synforge\frontend\components\layout\Sidebar.tsx v26 2026.07.31 - 최근 대화/Recent Meetings/Reviews 섹션 항상 표시(데이터 없어도 메뉴 유지, empty 상태 표시); 최근 대화는 branchAPI.conversations() 사용(instantAPI 는 branch 경로에서 저장되지 않음)
"use client"
import { useState, useEffect, useRef } from "react"
import { useRouter, usePathname } from "next/navigation"
import { useLocale } from '@/contexts/LocaleContext'
import {
  Home, FolderOpen, History, BarChart2,
  FileText, Image, Code, TrendingUp, Trash2,
  Cpu, Settings, ChevronDown,
  Zap, LogOut, User, Monitor, Users,
  Pencil, Check, X, CreditCard, Database
} from "lucide-react"
import { authAPI, instantAPI, branchAPI } from "@/lib/api"
import SettingsModal, { SettingsSection } from "@/components/settings/SettingsModal"
import { loadPersonalDisplaySettings } from "@/styles/personalDisplay"


export default function Sidebar({
  collapsed = false,
  onExpand = () => {},
}: {
  collapsed?: boolean
  onExpand?: () => void
}) {
  const router = useRouter()
  const pathname = usePathname()
  const { t } = useLocale()

  const [isAdmin, setIsAdmin] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [showNewMeetingConfirm, setShowNewMeetingConfirm] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settingsSection, setSettingsSection] = useState<SettingsSection | null>(null)

  const [chats, setChats] = useState<any[]>([])
  const [meets,   setMeets]   = useState<any[]>([])
  const [reviews, setReviews] = useState<any[]>([])
  const [resultsOpen, setResultsOpen] = useState(true)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editTitle, setEditTitle] = useState("")

  const accountMenuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target as Node)) {
        setSettingsOpen(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [])

    // 로고 — 테마(dark/light)에 따라 이미지 자체를 바꾼다. CSS display 토글 대신
  //   실제로 하나의 <img> 만 렌더링해서 두 개가 겹쳐 보이는 사고를 원천 차단한다.
  const [logoSrc, setLogoSrc] = useState("/logo_full_dark.png")
  useEffect(() => {
    // 최초 진입 시 서버에 저장된 테마값으로 초기화.
    loadPersonalDisplaySettings().then((s) => {
      setLogoSrc(s.theme === "light" ? "/logo_full_light.svg" : "/logo_full_dark.svg")
    })
    // 이후 Display 설정 화면에서 테마를 바꾸면 <html data-theme="..."> 속성이 즉시 바뀐다
    //   (applyPersonalDisplaySettings 가 함). 그 변화를 실시간으로 감지해 새로고침 없이
    //   로고를 바로 전환한다.
    const observer = new MutationObserver(() => {
      const theme = document.documentElement.getAttribute("data-theme")
      setLogoSrc(theme === "light" ? "/logo_full_light.svg" : "/logo_full_dark.svg")
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] })
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const stored = localStorage.getItem("user")
    if (stored) {
      try {
        const user = JSON.parse(stored)
        setIsAdmin(user.is_admin === true)
      } catch {
        setIsAdmin(false)
      }
    }
  }, [])

  useEffect(() => {
    // 최근 대화 — chat 창에서 오간 것은 분기(단답/회의/검증)와 무관하게
    //   BranchConversation/BranchTurn 하나에 저장된다(InstantConversation 은
    //   이 경로에서 채워지지 않으므로 쓰지 않는다).
    branchAPI.conversations()
      .then((res) => setChats(res.data.conversations || []))
      .catch(() => setChats([]))
    branchAPI.list()
      .then((res) => {
        const all = res.data.handoffs || []
        setMeets(all.filter((h: any) => h.kind === "meeting"))
        setReviews(all.filter((h: any) => h.kind === "review"))
      })
      .catch(() => { setMeets([]); setReviews([]) })
  }, [pathname])

  const isActive = (href: string) => pathname === href || pathname.startsWith(href + "/")
  const handleNav = (href: string) => { router.push(href) }

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      await authAPI.logout()
      router.push("/login")
    } catch {
      router.push("/login")
    } finally {
      setLoggingOut(false)
    }
  }

  const startEdit = (e: React.MouseEvent, c: any) => {
    e.stopPropagation()
    setEditingId(c.id)
    setEditTitle(c.preview || "")
  }

  const handleRename = async (id: number) => {
    const title = editTitle.trim()
    try {
      await instantAPI.renameConversation(id, title)
      setChats((prev) => prev.map((c) => (c.id === id ? { ...c, preview: title || c.preview } : c)))
    } catch (err) {
      console.error("Rename failed:", err)
    } finally {
      setEditingId(null)
      setEditTitle("")
    }
  }

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    try {
      await instantAPI.deleteConversation(id)
      setChats((prev) => prev.filter((c) => c.id !== id))
    } catch (err) {
      console.error("Delete failed:", err)
    }
  }

  const menuItems = [
    { icon: <FolderOpen size={15} />, label: t("nav.projects"), href: "/dashboard/projects" },
  ]

  const resultsItems = [
    { icon: <FileText size={15} />, label: t("nav.documents"), href: "/vault/documents" },
    { icon: <Image size={15} />, label: t("nav.images"), href: "/vault/images" },
    { icon: <Code size={15} />, label: t("nav.code"), href: "/vault/code" },
    { icon: <TrendingUp size={15} />, label: t("nav.reports"), href: "/vault/reports" },
  ]

  // Trash — 프로젝트/결과와 같은 레벨(최상위)의 독립 항목.
  const trashItem = { icon: <Trash2 size={15} />, label: "Trash", href: "/vault/trash" }

  // SettingsModal의 실제 탭 순서와 반드시 동일해야 한다: 프로필-결제-데이터-화면-워크스페이스-패널설정-AI모델-사용량-토큰-Statistics.
  // 이전 버전에서 결제/데이터 항목이 빠져 있어 계정 팝업 메뉴와 설정 모달 리스트가 서로 다르게 보였다.
  const settingsItems = [
    { icon: <User size={15} />, label: t("nav.profile"), href: "/settings/profile" },
    { icon: <CreditCard size={15} />, label: t("nav.billing"), href: "/settings/billing" },
    { icon: <Database size={15} />, label: t("nav.data"), href: "/settings/data" },
    { icon: <Monitor size={15} />, label: t("nav.display"), href: "/settings/display" },
    { icon: <Settings size={15} />, label: t("nav.workspace"), href: "/settings/workspace" },
    //{ icon: <Users size={15} />, label: t("nav.panelSet"), href: "/settings/panelset" },
    //{ icon: <Cpu size={15} />, label: t("nav.aiModels"), href: "/settings/models" },
    { icon: <BarChart2 size={15} />, label: t("nav.usage"), href: "/settings/usage" },
    { icon: <BarChart2 size={15} />, label: t("nav.tokens"), href: "/settings/tokens" },
    { icon: <BarChart2 size={15} />, label: t("nav.stats"), href: "/settings/stats" },
  ]

  const [userName, setUserName] = useState("H")
  const [tokenBalance, setTokenBalance] = useState<number | null>(null)
  useEffect(() => {
    const stored = localStorage.getItem("user")
    if (stored) {
      try {
        const user = JSON.parse(stored)
        setUserName(user.name || user.email?.charAt(0).toUpperCase() || "H")

      } catch { }
    }
  }, [])

  // 토큰 잔액 - 사이드바 상시 노출
  useEffect(() => {
    import("@/lib/api").then(({ billingAPI }) => {
      billingAPI.myTokens().then((res: any) => setTokenBalance(res.data.balance ?? 0)).catch(() => {})
    })
  }, [])

  const navBtn = (item: { icon: React.ReactNode; label: string; href: string }, indent = false) => (
    <button
      key={item.href}
      onClick={() => handleNav(item.href)}
      className={`w-full flex items-center gap-2.5 py-1 rounded-lg text-left transition-colors ${isActive(item.href)
        ? "bg-purple-600/20 text-purple-300"
        : "text-white/40 hover:text-white/70 hover:bg-white/[0.04]"
        } ${indent ? "pl-5" : "px-2.5"}`}
    >
      <span className={isActive(item.href) ? "text-purple-400" : ""}>{item.icon}</span>
      <span className="text-xs font-normal">{item.label}</span>
    </button>
  )

  // 접힘(아이콘 레일) 모드 — collapsed=true 면 라벨 없이 아이콘만 세로로
  //   나열한다. 클릭하면 그 페이지로 이동함과 동시에 onExpand() 로 부모
  //   (RootLayoutClient)의 collapsed 상태를 풀어 펼쳐진 사이드바를 보여준다
  //   (2026.09.19 Hugh 확정 — "레일 아이콘 클릭 시 이동 + 펼침").
  if (collapsed) {
    // 레일 아이콘 — 클릭하면 그 페이지로 "이동만" 한다. 사이드바를 펼치지
    //   않는다(2026.09.19 Hugh 확정 — "아이콘 누르면 작동을 해야지 메뉴가
    //   왜 열리냐"). 펼침/접힘은 오직 토글(화살표) 버튼으로만 한다.
    const railBtn = (item: { icon: React.ReactNode; label: string; href: string }) => (
      <button
        key={item.href}
        title={item.label}
        onClick={() => handleNav(item.href)}
        className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
          isActive(item.href)
            ? "bg-purple-600/20 text-purple-300"
            : "text-white/40 hover:text-white/70 hover:bg-white/[0.04]"
        }`}
      >
        {item.icon}
      </button>
    )

    return (
      <>
        {/* 회의 진행 중 확인 모달 — 전체(펼침) 모드에만 있고 레일 모드엔
            빠져 있어서, 새 회의 버튼을 눌러도 확인창이 안 뜨던 버그
            수정(Hugh 확인, 2026.09.19). */}
        {showNewMeetingConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-[#13131f] border border-white/[0.08] rounded-2xl p-6 w-80 shadow-2xl">
              <div className="flex items-center gap-2 mb-3">
                <Zap size={16} className="text-purple-400" />
                <span className="text-sm font-semibold text-white">Currently configuring...</span>
              </div>
              <p className="text-xs text-white/50 leading-relaxed mb-5">
                You are in the middle of setting up a meeting panel. Would you like to leave this page and start a new meeting?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowNewMeetingConfirm(false)
                    window.location.href = "/chat"
                  }}
                  className="flex-1 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-semibold transition-colors"
                >
                  Start Fresh
                </button>
                <button
                  onClick={() => setShowNewMeetingConfirm(false)}
                  className="flex-1 py-2 bg-white/[0.05] hover:bg-white/[0.08] text-white/50 rounded-lg text-xs transition-colors"
                >
                  Stay Here
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="w-14 h-screen bg-[#0d0d14] border-r border-white/[0.06] flex flex-col items-center shrink-0 py-4 gap-1">
          {/* 로고(축약) — 이동만, 펼치지 않는다 */}
        <button onClick={() => handleNav("/chat")} className="w-9 h-9 mb-2 flex items-center justify-center shrink-0">
          <img src={logoSrc} alt="Synforge" className="w-6 h-6 object-contain" />
        </button>

        {/* 새 회의 — 아이콘만, 자기 동작(회의 시작)만 하고 펼치지 않는다.
            window.location.href 로 전체 새로고침하면 리액트 상태(collapsed
            등)가 초기화됐다가 뒤늦게 다시 계산되는 사이 사이드바가 잠깐
            펼쳐진 것처럼 보였다 — router.push 로 클라이언트 전환해 상태를
            그대로 유지한다(Hugh 확인, 2026.09.19). */}
        <button
          onClick={() => {
            if (pathname === "/chat") setShowNewMeetingConfirm(true)
            else router.push("/chat")
          }}
          title={t("common.newMeeting")}
          className="w-9 h-9 mb-3 flex items-center justify-center bg-purple-600 hover:bg-purple-500 transition-colors rounded-lg shrink-0"
        >
          <Zap size={14} className="text-purple-100" />
        </button>

        {/* 메뉴 — 프로젝트/결과 허브/문서/이미지/코드/보고서/Trash */}
        <div className="flex-1 flex flex-col items-center gap-1 overflow-y-auto">
          {menuItems.map((item) => railBtn(item))}
          {railBtn({ icon: <BarChart2 size={15} />, href: "/dashboard/results", label: t("nav.resultsHub") })}
          {resultsItems.map((item) => railBtn(item))}
          {railBtn(trashItem)}
        </div>

        {/* 계정 — 유일한 예외. 계정 클릭의 "동작" 자체가 설정 메뉴를 보는
            것이라 펼침이 필요하다(나머지 아이콘과 성격이 다름). */}
          <button
            onClick={onExpand}
            title={userName}
            className="w-9 h-9 rounded-full bg-purple-600/30 border border-purple-500/30 flex items-center justify-center shrink-0"
          >
            <span className="text-xs font-bold text-purple-300">{userName.charAt(0).toUpperCase()}</span>
          </button>
        </div>
      </>
    )
  }

  return (
    <div className="w-[200px] h-screen bg-[#0d0d14] border-r border-white/[0.06] flex flex-col shrink-0">
      {showNewMeetingConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#13131f] border border-white/[0.08] rounded-2xl p-6 w-80 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Zap size={16} className="text-purple-400" />
              <span className="text-sm font-semibold text-white">Currently configuring...</span>
            </div>
            <p className="text-xs text-white/50 leading-relaxed mb-5">
              You are in the middle of setting up a meeting panel. Would you like to leave this page and start a new meeting?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowNewMeetingConfirm(false)
                  window.location.href = "/chat"
                }}
                className="flex-1 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-semibold transition-colors"
              >
                Start Fresh
              </button>
              <button
                onClick={() => setShowNewMeetingConfirm(false)}
                className="flex-1 py-2 bg-white/[0.05] hover:bg-white/[0.08] text-white/50 rounded-lg text-xs transition-colors"
              >
                Stay Here
              </button>
            </div>
          </div>
        </div>
      )}

      <SettingsModal
        section={settingsSection}
        onClose={() => setSettingsSection(null)}
        onSectionChange={setSettingsSection}
      />

      {/* 로고 — 아래 New Meeting 버튼과 같은 px-3 컨테이너 폭에 맞춰 비율을 맞춘다. */}
      <div className="px-3 py-4 cursor-pointer" onClick={() => handleNav("/chat")}>
        <img src={logoSrc} alt="Synforge" className="w-full h-auto object-contain" />
      </div>

      {/* 새 회의 버튼 */}
      <div className="px-3 mb-4">
        <button
          onClick={() => {
            if (pathname === "/chat") {
              setShowNewMeetingConfirm(true)
            } else {
              window.location.href = "/chat"
            }
          }}
          className="w-full flex items-center gap-2 bg-purple-600 hover:bg-purple-500 transition-colors rounded-lg px-3 py-2"
        >
          <Zap size={13} className="text-purple-200 shrink-0" />
          <span className="text-white text-xs font-semibold">{t("common.newMeeting")}</span>
        </button>
      </div>

      {/* 스크롤 영역: 프로젝트 -> 결과(폴더, 하위에 최근대화/회의/리뷰/결과허브/문서/이미지/코드/보고서/Trash) */}
      <div className="flex-1 overflow-y-auto px-3 space-y-1">

        {/* 프로젝트 */}
        <div>
          {menuItems.map((item) => navBtn(item))}
        </div>

        {/* 결과 — 폴더 버튼. */}
        <div>
          <button
            onClick={() => setResultsOpen(!resultsOpen)}
            className="w-full flex items-center gap-2.5 px-2.5 py-1 rounded-lg text-left text-white/40 hover:text-white/70 hover:bg-white/[0.04] transition-colors"
          >
            <BarChart2 size={15} />
            <span className="flex-1 text-xs font-normal">{t("nav.sectionResults")}</span>
            <ChevronDown size={12} className={`text-white/20 transition-transform duration-200 ${resultsOpen ? "rotate-180" : ""}`} />
          </button>

          {resultsOpen && (
            <div className="pl-2 space-y-2 mt-1">

              {/* 최근 대화 — 데이터가 없어도 섹션은 항상 보인다(비어있음과 API 실패를 구분하기 위함). */}
              <div>
                <button
                  onClick={() => handleNav("/recents_chat")}
                  className="text-[10px] font-semibold text-white/20 tracking-widest uppercase hover:text-white/50 transition-colors px-2 mb-1 block"
                >
                  {t("nav.recents")}
                </button>
                {chats.length === 0 ? (
                  <p className="px-2.5 py-1 text-xs text-white/15">(empty)</p>
                ) : (
                  <div className="space-y-0">
                    {chats.slice(0, 5).map((c) => (
                      <div
                        key={c.id}
                        className="group relative flex items-center rounded-lg text-white/40 hover:text-white/70 hover:bg-white/[0.04] transition-colors"
                      >
                        {editingId === c.id ? (
                          <div className="flex items-center gap-1 w-full px-2 py-1">
                            <input
                              autoFocus
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleRename(c.id)
                                if (e.key === "Escape") { setEditingId(null); setEditTitle("") }
                              }}
                              className="flex-1 min-w-0 bg-white/[0.06] border border-white/10 rounded px-1.5 py-1 text-xs text-white outline-none"
                            />
                            <button onClick={() => handleRename(c.id)} className="text-green-400 hover:text-green-300 shrink-0" title="Save">
                              <Check size={13} />
                            </button>
                            <button onClick={() => { setEditingId(null); setEditTitle("") }} className="text-white/30 hover:text-white/60 shrink-0" title="Cancel">
                              <X size={13} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleNav(`/chat?conversation_id=${c.id}`)}
                              className="flex-1 min-w-0 flex items-center px-2.5 py-1 text-left"
                              title={c.preview}
                            >
                              <span className="text-xs font-normal truncate">{c.preview}</span>
                            </button>
                            <div className="absolute right-1 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 bg-[#0d0d14] pl-1">
                              <button onClick={(e) => startEdit(e, c)} className="p-1 text-white/30 hover:text-white/70" title="Rename">
                                <Pencil size={12} />
                              </button>
                              <button onClick={(e) => handleDelete(e, c.id)} className="p-1 text-white/30 hover:text-red-400" title="Delete">
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Meetings — 데이터가 없어도 섹션은 항상 보인다. */}
              <div>
                <button
                  onClick={() => handleNav("/history1")}
                  className="text-[10px] font-semibold text-white/20 tracking-widest uppercase hover:text-white/50 transition-colors px-2 mb-1 block"
                >
                  {t("nav.recentsMeet")}
                </button>
                {meets.length === 0 ? (
                  <p className="px-2.5 py-1 text-xs text-white/15">(empty)</p>
                ) : (
                  <div className="space-y-0">
                    {meets.slice(0, 5).map((m) => (
                      <button
                        key={m.id}
                        onClick={() => handleNav(m.field?.startsWith("mediaverify") ? `/mediaverify/${m.id}` : `/key_meeting/${m.id}`)}
                        className="w-full flex items-center px-2.5 py-1 text-left rounded-lg text-white/40 hover:text-white/70 hover:bg-white/[0.04] transition-colors"
                        title={m.agenda}
                      >
                        <span className="text-xs font-normal truncate">{m.agenda}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Reviews — 데이터가 없어도 섹션은 항상 보인다. */}
              <div>
                <button
                  onClick={() => handleNav("/reviews")}
                  className="text-[10px] font-semibold text-white/20 tracking-widest uppercase hover:text-white/50 transition-colors px-2 mb-1 block"
                >
                  {t("nav.recentsReview")}
                </button>
                {reviews.length === 0 ? (
                  <p className="px-2.5 py-1 text-xs text-white/15">(empty)</p>
                ) : (
                  <div className="space-y-0">
                    {reviews.slice(0, 5).map((r) => (
                      <button
                        key={r.id}
                        onClick={() => handleNav(`/review-view/${r.id}`)}
                        className="w-full flex items-center px-2.5 py-1 text-left rounded-lg text-white/40 hover:text-white/70 hover:bg-white/[0.04] transition-colors"
                        title={r.agenda}
                      >
                        <span className="text-xs font-normal truncate">{r.agenda}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 결과 허브 */}
              {navBtn({ icon: <BarChart2 size={15} />, label: t("nav.resultsHub"), href: "/dashboard/results" })}

              {/* 문서/이미지/코드/보고서 */}
              {resultsItems.map((item) => navBtn(item))}

            </div>
          )}
        </div>

        {/* Trash — 최상위(프로젝트/결과와 같은 레벨) */}
        <div>
          {navBtn(trashItem)}
        </div>

      </div>

      {/* 하단 유저 정보 */}
      <div className="px-3 py-3 border-t border-white/[0.06] relative" ref={accountMenuRef}>
        {settingsOpen && (
          <div className="absolute bottom-full left-3 right-3 mb-2 bg-[#13131f] border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden py-1">
            {settingsItems.map((item) => {
              const sectionId = item.href.split("/").pop() as SettingsSection
              return (
                <button
                  key={item.href}
                  onClick={() => { setSettingsSection(sectionId); setSettingsOpen(false) }}
                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors ${settingsSection === sectionId
                    ? "bg-purple-600/20 text-purple-300"
                    : "text-white/60 hover:text-white/90 hover:bg-white/[0.05]"
                    }`}
                >
                  <span className={settingsSection === sectionId ? "text-purple-400" : ""}>{item.icon}</span>
                  <span className="text-xs font-normal">{item.label}</span>
                </button>
              )
            })}
            <div className="my-1 border-t border-white/[0.06]" />
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-white/60 hover:text-red-400 hover:bg-white/[0.05] transition-colors"
            >
              <LogOut size={15} />
              <span className="text-xs font-normal">{t("nav.logout")}</span>
            </button>
          </div>
        )}
        <button
          onClick={() => setSettingsOpen(!settingsOpen)}
          className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-white/[0.04] transition-colors"
        >
          <div className="w-7 h-7 rounded-full bg-purple-600/30 border border-purple-500/30 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-purple-300">
              {userName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="text-xs font-normal text-white truncate">{userName}</div>
            <div className="text-[10px] text-white/25 truncate">{tokenBalance !== null ? `${tokenBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tokens` : "..."}</div>
          </div>
          <ChevronDown size={12} className={`text-white/20 transition-transform duration-200 ${settingsOpen ? "" : "rotate-180"}`} />
        </button>
      </div>

    </div>
  )
}